import { Box, SxProps } from "@mui/system";

import { resolveIcon } from "../../pm-components/utils/resolve-icon";
import { SideMenuEntryInterface } from "../../types/menu-type";

export type ExtendedSideMenuEntryInterface = SideMenuEntryInterface & {
  disabled?: boolean;
  icon?: string;
  iconProps?: SxProps;
};

interface MenuTabProps {
  entries: ExtendedSideMenuEntryInterface[];
  onNavigate: (link: string) => void;
  activeTab: string | null;
}

export function DialogLayoutSideBar({
  entries,
  onNavigate,
  activeTab,
}: Readonly<MenuTabProps>) {
  return (
    <>
      {entries.map((entry) => (
        <FirstLevelTab
          key={entry.viewname}
          tab={entry}
          onNavigate={onNavigate}
          activeTab={activeTab}
        />
      ))}
    </>
  );
}

function FirstLevelTab({
  tab,
  onNavigate,
  activeTab,
}: Readonly<{
  tab: ExtendedSideMenuEntryInterface;
  onNavigate: (link: string) => void;
  activeTab: string | null;
}>) {
  const isActive = activeTab === tab.link;
  const resolvedIcon = resolveIcon(tab.icon);

  const handleTabClick = () => {
    if (tab.link && !tab.disabled) {
      onNavigate(tab.link);
    }
  };

  return (
    <>
      <Box
        onClick={handleTabClick}
        sx={(theme) => ({
          cursor: "pointer",
          height: "28px",
          fontSize: "16px",
          fontWeight: isActive ? 600 : 400,
          borderLeft: `${
            isActive ? theme.palette.primary.main : "#B2CED4"
          } 2px solid`,
          color: `${
            tab.disabled
              ? "#cecece"
              : isActive
                ? theme.palette.primary.main
                : "#000000"
          }`,
          padding: "32px 4px 32px 11px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          transition: "border-left-color 0.3s",
        })}
      >
        {tab.title}
        {resolvedIcon && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mr: "4px",
              ...tab.iconProps,
            }}
          >
            {resolvedIcon}
          </Box>
        )}
      </Box>
      {tab.items && (
        <Box
          sx={{
            maxHeight: "200px",
            transition: "max-height 1s",
            overflow: "hidden",
          }}
        >
          {tab.items?.map((childTab: ExtendedSideMenuEntryInterface) => (
            <SecondLevelTab
              tab={childTab}
              key={childTab.link}
              onNavigate={onNavigate}
              activeTab={activeTab}
            />
          ))}
        </Box>
      )}
    </>
  );
}

function SecondLevelTab({
  tab,
  onNavigate,
  activeTab,
}: {
  tab: ExtendedSideMenuEntryInterface;
  onNavigate: (link: string) => void;
  activeTab: string | null;
}) {
  const isActive = activeTab === tab.link;
  const resolvedIcon = resolveIcon(tab.icon);
  const handleTabClick = () => {
    if (tab.disabled) {
      return;
    }
    if (tab.link) {
      onNavigate(tab.link);
    }
  };

  return (
    <Box
      onClick={handleTabClick}
      sx={(theme) => ({
        cursor: "pointer",
        height: "10px",
        fontSize: "14px",
        fontWeight: isActive ? 600 : 400,
        borderLeft: `${
          isActive ? theme.palette.primary.main : "#B2CED4"
        } 2px solid`,
        color: `${
          tab.disabled
            ? "#cecece"
            : isActive
              ? theme.palette.primary.main
              : "#000000"
        }`,
        padding: "16px 4px 16px 24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        transition: "border-left-color 0.3s",
      })}
    >
      {tab.title}
      {resolvedIcon && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mr: "4px",
            ...tab.iconProps,
          }}
        >
          {resolvedIcon}
        </Box>
      )}
    </Box>
  );
}
